// Session-local conflict outcomes — the "one item, two views" glue (redesign port).
//
// A resolved/snoozed conflict disappears from the SERVER's brief payload only when the brief
// regenerates, so the stored row keeps carrying the task all day. Every surface that renders
// conflicts (Briefing cards, the TopNav Actions badge, the Actions workroom) therefore also
// consults this in-memory record of what the user already did THIS SESSION. It is not
// persistence — the server rails (decision_resolutions / decision_conflict_snooze /
// decision_conflict_state) are the durable truth and win on the next payload.
//
// Verb semantics mirror the resolve route: decision/custom/not_conflict are TERMINAL (the
// conflict leaves open work), snooze PAUSES it (per-user), blocked keeps it OPEN with the
// blocker named, ping is not an outcome at all (never recorded here).

import { useSyncExternalStore } from 'react';

export type ConflictVerb = 'decision' | 'custom' | 'not_conflict' | 'snooze' | 'blocked';

const SESSION_RECORDED_VERBS: readonly ConflictVerb[] = ['decision', 'custom', 'not_conflict', 'snooze', 'blocked'];

/**
 * Whether a resolve `choice` is one this store models. The resolve route accepts MORE verbs than this
 * (ping / route / object), and they must NOT be recorded: `outcomeClears` treats anything but `blocked`
 * as cleared, so storing a `route` would drop a still-OPEN conflict out of the brief, the badge and the
 * Actions lane.
 *
 * It exists as a narrowing predicate rather than a hand-written `!==` guard at each call site because
 * the guard was the thing that got missed: one surface excluded route, its sibling didn't, and an
 * `as ConflictVerb` cast made the mismatch invisible to tsc. Call sites now narrow instead of casting,
 * so a verb added to the API is excluded by default rather than silently coerced.
 */
export function isSessionRecordedVerb(choice: string): choice is ConflictVerb {
  return (SESSION_RECORDED_VERBS as readonly string[]).includes(choice);
}

export interface ConflictOutcome {
  verb: ConflictVerb;
  /** Human line for the session record — e.g. the chosen decision text or the custom resolution. */
  how?: string;
  /** Snooze flavor, for the paused lane's wake label. */
  wake?: string;
  /** The conflict's title at act time (the brief payload may be gone when the record renders). */
  title?: string;
}

let outcomes = new Map<string, ConflictOutcome>();
const listeners = new Set<() => void>();

export function recordConflictOutcome(edgeKey: string, outcome: ConflictOutcome): void {
  const next = new Map(outcomes);
  next.set(edgeKey, outcome);
  outcomes = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): Map<string, ConflictOutcome> {
  return outcomes;
}

/** Reactive read of the session's conflict outcomes, keyed by edgeKey (briefingItemId). */
export function useConflictOutcomes(): Map<string, ConflictOutcome> {
  // Third arg: server snapshot — renderToString (the unit suite) requires it.
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** True when the outcome removes the conflict from "needs you" (terminal verbs + snooze). */
export function outcomeClears(outcome: ConflictOutcome | undefined): boolean {
  return !!outcome && outcome.verb !== 'blocked';
}
